import { useState, useEffect, useRef, useCallback } from 'react';
// API
import API from '../API';
// Helpers
import { isPersistedState, persistState } from '../helpers';

const initialState = {
  page: 0,
  results: [],
  total_pages: 0,
  total_results: 0
};

export const useHomeFetch = searchTerm => {
  const [state, setState] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Ignore responses from searches the user has already moved past
  const requestId = useRef(0);

  const fetchMovies = useCallback(async (page, term = '') => {
    const id = ++requestId.current;
    try {
      setError(false);
      setLoading(true);

      const movies = await API.fetchMovies(term, page);
      if (!movies || !Array.isArray(movies.results)) throw new Error('Bad response');
      if (id !== requestId.current) return;

      setState(prev => ({
        ...movies,
        results:
          page > 1
            ? [
                ...prev.results,
                ...movies.results.filter(
                  movie => !prev.results.some(p => p.id === movie.id)
                )
              ]
            : [...movies.results]
      }));
    } catch (error) {
      if (id === requestId.current) setError(true);
    }
    if (id === requestId.current) setLoading(false);
  }, []);

  // Search and initial
  useEffect(() => {
    if (!searchTerm) {
      const sessionState = isPersistedState('homeState');

      if (sessionState && sessionState.results && sessionState.results.length) {
        requestId.current++;
        setState(sessionState);
        setLoading(false);
        return;
      }
    }
    setState(initialState);
    fetchMovies(1, searchTerm);
  }, [searchTerm, fetchMovies]);

  // Write to sessionStorage
  useEffect(() => {
    if (!searchTerm && state.results.length) persistState('homeState', state);
  }, [searchTerm, state]);

  const loadMore = () => fetchMovies(state.page + 1, searchTerm);
  const retry = () => fetchMovies(state.page ? state.page + 1 : 1, searchTerm);

  return { state, loading, error, loadMore, retry };
};
